import { StringManager } from '@payments-checkout/shared/helpers/index';
import { analiticaInstance as analiticaInstanceConfigs } from './analitica.configs';
import { DigitalWallets } from './analitica.enum';
import {
  AllPaymentMethodsType,
  DigitalWalletsStatusType,
  DigitalWalletsType
} from './analitica.types';

const FIELD_DESCRIPTIONS: Record<string, string> = {
  campo_email: 'Campo email preenchido',
  campo_nome: 'Campo nome preenchido',
  campo_documento: 'Campo documento preenchido',
  campo_telefone: 'Campo telefone preenchido',
};

const DROPDOWN_DESCRIPTIONS: Record<string, string> = {
  select_documento: 'Tipo de documento selecionado',
  select_cod_pais: 'Código de país selecionado',
};

const CARD_FIELD_DESCRIPTIONS: Record<string, string> = {
  campo_numero_cartao: 'Campo Número do cartão preenchido',
  campo_nome_cartao: 'Campo Nome no cartão preenchido',
  campo_vencimento_cartao: 'Campo Vencimento preenchido',
  campo_cvv_cartao: 'Campo CVV preenchido',
};

const ADDRESS_FIELD_DESCRIPTIONS: Record<string, string> = {
  campo_cep: 'Campo CEP preenchido',
  campo_cidade: 'Campo Cidade preenchido',
  campo_endereco: 'Campo de endereço preenchido',
  campo_bairro: 'Campo Bairro preenchido',
  campo_numero: 'Campo número preenchido',
  campo_complemento: 'Campo complemento preenchido',
};

const AMPLITUDE_ONLY = ['AMPLITUDE_CHECKOUT_V3'];

/**
 * Evento de produto, exclusivo do Amplitude. O `DatadogPlugin` transformava cada
 * um em action no RUM mais log indexado, sem valor de debug.
 *
 * Nos dois destinos ficam só erro, marco do funil de pagamento e `page-viewed`.
 */
const amplitudeOnly = <T extends { label: string; metadata?: object }>(
  event: T
) => ({ ...event, filterPlugins: AMPLITUDE_ONLY });

export const analiticaInstance = analiticaInstanceConfigs.eventFactory(
  'Checkout v3',
  {
    errorPage: {
      loadErrorViewed: (
        errorType: string,
        defaultLanguage: string,
        project: string,
        hasDelivery?: boolean
      ) => ({
        label: 'page-viewed',
        metadata: {
          screen: 'erro_carregamento',
          reference: 'tela',
          error_type: errorType,
          default_language: defaultLanguage,
          description: 'Visualização de erro generico',
          ...(project ? { project } : {}),
          ...(hasDelivery === undefined ? {} : { has_delivery: hasDelivery }),
        },
      }),
    },
    expirationModal: {
      sessionExpiredViewed: (screen: string) => ({
        label: 'modal-viewed',
        metadata: {
          screen,
          reference: 'modal_sessao_expirada',
          description: 'Sessão expirada'
        },
      }),
      refreshPageClicked: (screen: string) => amplitudeOnly({
        label: 'button-clicked',
        metadata: {
          screen,
          reference: 'botao_atualizar_modal_sessao_expirada',
          description: 'Clique no botão Atualizar página'
        },
      }),
    },
    homePage: {
      viewPage: (origin: string, defaultPaymentType: string, defaultLanguage: string, hasDelivery?: boolean) => ({
        label: 'page-viewed',
        metadata: {
          reference: 'tela',
          screen: 'inicial',
          default_payment_type: defaultPaymentType,
          default_language: defaultLanguage,
          origin,
          description: 'Visualização da etapa inicial',
          ...(hasDelivery === undefined ? {} : { has_delivery: hasDelivery }),
        },
      }),
      languageSelected: (screen: string, language: string) => amplitudeOnly({
        label: 'item-selected',
        metadata: {
          screen,
          reference: 'opcao_linguagem',
          selected_language: language,
          description: 'Linguagem selecionada'
        },
      }),
      paymentMethodClicked: (type: AllPaymentMethodsType) => amplitudeOnly({
        label: 'button-clicked',
        metadata: {
          reference: 'selecao_forma_pagamento',
          screen: 'inicial',
          payment_type: type,
          description: 'Forma de pagamento selecionada'
        },
      }),
      formFieldFilled: (type: string) => amplitudeOnly({
        label: 'field-filled',
        metadata: {
          screen: 'inicial',
          reference: type,
          description: FIELD_DESCRIPTIONS[type] ?? ''
        },
      }),
      formFieldError: (type: string, errorType: string) => ({
        label: 'error-viewed',
        metadata: {
          screen: 'inicial',
          reference: type,
          error_type: errorType,
          description: 'Erro no preenchimento do formulário'
        },
      }),
      formDropdownSelected: (type: string, selectedOption: string) => amplitudeOnly({
        label: 'dropdown-clicked',
        metadata: {
          screen: 'inicial',
          reference: type,
          selected_option: selectedOption,
          description: DROPDOWN_DESCRIPTIONS[type] ?? ''
        },
      }),
      detailsClicked: (screen: string) => amplitudeOnly({
        label: 'button-clicked',
        metadata: {
          screen,
          reference: 'botao_detalhes',
          description: 'Clique no botão Detalhes'
        },
      }),
      paymentErrorViewed: (errorType: string) => ({
        label: 'error-viewed',
        metadata: {
          screen: 'inicial',
          reference: 'alert_erro',
          error_type: errorType,
          description: 'Erro na finalização do pagamento'
        },
      }),
      submitData: (paymentType: string) => ({
        label: 'button-clicked',
        metadata: {
          screen: 'inicial',
          reference: 'botao_continuar',
          selected_payment_type: paymentType,
          description: 'Clique no botão Continuar'
        },
      }),
      /**
       * @deprecated Formato antigo, mantido porque não há equivalente nos prints
       * deste lote — pertence ao lote de Carteiras Digitais, remover depois da migração completa.
       */
      digitalWalletsPaymentStatus: (
        status: DigitalWalletsStatusType,
        digitalWallet: DigitalWallets
      ) => ({
        label: 'checkout v3 - digital wallets payment status',
        metadata: {
          status,
          digitalWallet,
          paymentLinkId: StringManager.getParamBetweenSlash({
            url: window.location.pathname,
          }),
        },
      }),
    },
    addressEvents: {
      deliveryAddressPageViewed: () => ({
        label: 'page-viewed',
        metadata: {
          screen: 'endereco_entrega',
          reference: 'tela',
          description: 'Visualização da etapa endereço de entrega'
        },
      }),
      deliveryAddressContinueClicked: () => ({
        label: 'button-clicked',
        metadata: {
          screen: 'endereco_entrega',
          reference: 'botao_continuar',
          description: 'Clique no botão Continuar'
        },
      }),
      deliveryAddressBackClicked: () => amplitudeOnly({
        label: 'button-clicked',
        metadata: {
          screen: 'endereco_entrega',
          reference: 'botao_voltar',
          description: 'Clique no botão Voltar'
        },
      }),
      zipCodeHelpClicked: (screen: string) => amplitudeOnly({
        label: 'link-clicked',
        metadata: {
          screen,
          reference: 'link_nao_sei_cep',
          description: 'Clique no link Não sei meu CEP'
        },
      }),
      addressFieldFilled: (screen: string, reference: string) => amplitudeOnly({
        label: 'field-filled',
        metadata: {
          screen,
          reference,
          description: ADDRESS_FIELD_DESCRIPTIONS[reference] ?? ''
        },
      }),
      addressFieldError: (
        screen: string,
        reference: string,
        errorType: string
      ) => ({
        label: 'error-viewed',
        metadata: {
          screen,
          reference,
          error_type: errorType,
          description: 'Erro no preenchimento do formulário'
        },
      }),
      addressCountrySelected: (screen: string, addressCountry: string) => amplitudeOnly({
        label: 'dropdown-clicked',
        metadata: {
          screen,
          reference: 'select_pais',
          address_country: addressCountry,
          description: 'Clique no select País'
        },
      }),
      addressStateSelected: (screen: string, addressState: string) => amplitudeOnly({
        label: 'dropdown-clicked',
        metadata: {
          screen,
          reference: 'select_estado',
          address_state: addressState,
          description: 'Clique no select Estado'
        },
      }),
      withoutNumberSelected: (screen: string, isSelected: boolean) => amplitudeOnly({
        label: 'checkbox-selected',
        metadata: {
          screen,
          reference: 'checkbox_sem_numero',
          is_selected: isSelected,
          description: 'Clique no checkbox Sem número'
        },
      }),
      deliveryAddressError: () => ({
        label: 'error-viewed',
        metadata: {
          screen: 'endereco_entrega',
          reference: 'erro_raio_entrega',
          description: 'Erro de raio de entrega'
        },
      }),
    },
    billingPage: {
      pageViewed: (fakedoorToggleChecked?: boolean) => ({
        label: 'page-viewed',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'tela',
          description: 'Visualização da etapa de dados de cobrança',
          ...(fakedoorToggleChecked !== undefined
            ? { fakedoor_toggle_checked: fakedoorToggleChecked }
            : {}),
        },
      }),
      cardFieldFilled: (reference: string) => amplitudeOnly({
        label: 'field-filled',
        metadata: {
          screen: 'dados_cobranca',
          reference,
          description: CARD_FIELD_DESCRIPTIONS[reference] ?? ''
        },
      }),
      installmentsSelected: (installments: string) => amplitudeOnly({
        label: 'dropdown-clicked',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'select_parcelas',
          installments,
          description: 'Clique no select Parcelas'
        },
      }),
      finishClicked: (
        selectedInstallments?: string,
        cardBrand?: string,
        fakedoorToggleChecked?: boolean
      ) => ({
        label: 'button-clicked',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'botao_finalizar',
          description: 'Clique no botão Finalizar',
          ...(selectedInstallments
            ? { selected_installments: selectedInstallments }
            : {}),
          ...(cardBrand ? { card_brand: cardBrand } : {}),
          ...(fakedoorToggleChecked !== undefined
            ? { fakedoor_toggle_checked: fakedoorToggleChecked }
            : {}),
        },
      }),
      backClicked: () => amplitudeOnly({
        label: 'button-clicked',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'botao_voltar',
          description: 'Clique no botão Voltar'
        },
      }),
      fieldError: (reference: string, errorType: string) => ({
        label: 'error-viewed',
        metadata: {
          screen: 'dados_cobranca',
          reference,
          error_type: errorType,
          description: 'Erro no preenchimento do formulário'
        },
      }),
      paymentErrorViewed: (errorType: string) => ({
        label: 'error-viewed',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'alert_erro',
          error_type: errorType,
          description: 'Erro na finalização do pagamento'
        },
      }),
      editDeliveryAddressClicked: () => amplitudeOnly({
        label: 'button-clicked',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'botao_editar_endereco_entrega',
          description: 'Clique em editar endereço de entrega'
        },
      }),
      sameAddressSelected: (isSelected: boolean) => amplitudeOnly({
        label: 'checkbox-selected',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'checkbox_endereco_cobranca',
          is_selected: isSelected,
          description: 'Clique no checkbox Usar o mesmo endereço de entrega'
        },
      }),
      automaticChargeOptInSelected: (isSelected: boolean) => amplitudeOnly({
        label: 'checkbox-selected',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'checkbox_optin_cobranca_automatica',
          is_selected: isSelected,
          description: 'Clique checkbox no opt-in cobrança automática'
        },
      }),
      fakedoorTooltipInteracted: () => amplitudeOnly({
        label: 'button-clicked',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'icone_tooltip_fakedoor_toggle',
          description: 'Hover ou clique no ícone de info'
        },
      }),
      fakedoorToggleClicked: (isChecked: boolean) => amplitudeOnly({
        label: 'checkbox-clicked',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'fakedoor_toggle',
          fakedoor_toggle_checked: isChecked,
          description: 'Clique no toggle fakedoor'
        },
      }),
      cardTokenized: (success: boolean) => ({
        label: 'feedback-action',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'tokenizacao',
          success,
          description: 'Cartão tokenizado'
        },
      }),
      threeDsTokenized: (
        success: boolean,
        card?: { firstSixDigits: string; lastFourDigits: string }
      ) => ({
        label: 'feedback-action',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'tokenizacao_3ds',
          success,
          description: 'Tokenização 3DS',
          ...(card && {
            first_six_digits: card.firstSixDigits,
            last_four_digits: card.lastFourDigits,
          }),
        },
      }),
      threeDsProcessed: () => ({
        label: 'feedback-action',
        metadata: {
          screen: 'dados_cobranca',
          reference: 'processo_3ds',
          description: 'Processo 3DS'
        },
      }),
    },
    pixPage: {
      pageViewed: () => ({
        label: 'checkout v3 - pix page viewed',
        metadata: { paymentLinkId: window.location.pathname },
      }),
      copyClicked: () => amplitudeOnly({
        label: 'checkout v3 - pix copy clicked',
        metadata: {
          paymentLinkId: StringManager.getParamBetweenSlash({
            url: window.location.pathname,
          }),
        },
      }),
    },
    boletoPage: {
      copyClicked: () => ({
        label: 'checkout v3 - invoice code copy clicked',
        metadata: {
          paymentLinkId: StringManager.getParamBetweenSlash({
            url: window.location.pathname,
          }),
        },
      }),
      downloadClicked: () => amplitudeOnly({
        label: 'checkout v3 - invoice download clicked',
        metadata: {
          paymentLinkId: StringManager.getParamBetweenSlash({
            url: window.location.pathname,
          }),
        },
      }),
    },
    paymentPage: {
      digitalWalletsFinishClick: (digitalWallet: DigitalWalletsType) => ({
        label: 'checkout v3 - wallets address page finish clicked',
        metadata: {
          digitalWallet,
          paymentLinkId: StringManager.getParamBetweenSlash({
            url: window.location.pathname,
          }),
        },
      }),
    },
    creditCardPage: {
      successCreditCardViewed: () => ({
        label: 'checkout v3 - credit card payment success viewed',
        metadata: {
          paymentLinkId: StringManager.getParamBetweenSlash({
            url: window.location.pathname,
          }),
        },
      }),
      successDigitalWalletsViewed: (digitalWallets: DigitalWalletsType) => ({
        label: 'checkout v3 - digital wallets payment success viewed',
        metadata: {
          digitalWallets,
          paymentLinkId: StringManager.getParamBetweenSlash({
            url: window.location.pathname,
          }),
        },
      }),
    },
    deliveryEvents: {
      trackingButtonClicked: () => amplitudeOnly({
        label: 'button-clicked',
        metadata: {
          screen: 'conclusao',
          reference: 'botao_rastreio',
          description: 'Clique no botão ’Acompanhar entrega’'
        },
      }),
    }
  }
);
